/**
 * useUrlIndexOverview Hook
 * 
 * React Query hook for fetching URL index overview data.
 * Uses the unified url-index-overview endpoint.
 */

import { useQuery } from '@tanstack/react-query';
import { getUrlIndexOverview, GetUrlIndexOverviewParams } from '@/services/customer/urlCoverageApi';
import { UrlIndexOverviewResponse } from '@/types/customer';
import { useSiteStore } from '@/store/site.store';

export interface UseUrlIndexOverviewOptions extends GetUrlIndexOverviewParams {
  siteId?: number;
  enabled?: boolean;
}

export function useUrlIndexOverview(options: UseUrlIndexOverviewOptions = {}) {
  const selectedSite = useSiteStore((state) => state.selectedSite);
  const siteId = options.siteId ?? selectedSite?.id ?? 1;

  return useQuery<UrlIndexOverviewResponse, Error>({
    queryKey: ['url-index-overview', siteId, options.engine],
    queryFn: () => getUrlIndexOverview(siteId, {
      engine: options.engine,
    }),
    enabled: options.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Legacy alias
export const useUrlCoverage = useUrlIndexOverview;
export type UseUrlCoverageOptions = UseUrlIndexOverviewOptions;

export default useUrlIndexOverview;
